import React, { ReactNode, ReactElement, useEffect } from 'react'
import { Link } from 'react-router-dom'
import isEmpty from 'lodash/isEmpty'
import history from 'shared/utils/history'
import { useSelector, useDispatch } from 'react-redux'
import { GlobalState } from 'shared/redux/reducer'
import { createSelector } from 'reselect'
import { setOpenedList, setActive } from 'shared/redux/sidebar/reducer'
import styled from 'styled-components'
import { RouteRoleLevel, PermissionId } from 'shared/types'
import { useMenu } from '../MenuContext'
import { organizationSettingSelector } from 'shared/hocs/withAuth'
export interface SubNavType {
  to: string
  label: string | ReactNode | ReactElement | JSX.Element
  requiredRoles?: Array<string>
  icon?: string | ReactNode
  iconActive?: string | ReactNode
  exact?: boolean
  level: RouteRoleLevel
  permissions: PermissionId[]
  isKioskAllowed?: boolean
  orgTypes?: string[]
  orgExclusiveTypes?: string[]
  features?: string[]
  isButton?: boolean
  onButtonClick?: () => void
}

interface ListProps {
  label: string | ReactNode
  icon: string | ReactNode
  to: string
  iconActive: string | ReactNode
  links?: string[]
  subnav: Array<SubNavType>
}

export const navListSelector = createSelector(
  (state: GlobalState) => state.sidebar.active,
  active => ({ active }),
)

const NavigationList: React.FC<ListProps> = (props: ListProps) => {
  const { active } = useSelector(navListSelector)
  const dispatch = useDispatch()
  const { organization_information } = useSelector(organizationSettingSelector)
  const menu = useMenu()

  const path = `/${active.split('/')[1]}`
  const isNavActive = path === props.to || props.subnav.find(sub => sub.to === path)

  useEffect(() => {
    dispatch(setActive(history.location.pathname))
    dispatch(setOpenedList(`/${history.location.pathname.split('/')[1]}`))
  }, [history.location.pathname])

  return (
    <NavListWrapper>
      {isEmpty(props.subnav) && (
        <ListItem to={props.to} onClick={menu.hide}>
          <ItemDetails>
            <IconContainer>
              {typeof props.icon === 'string' && typeof props.iconActive === 'string' ? (
                <img src={isNavActive ? props.iconActive : props.icon} className="icon" alt="Icon" />
              ) : !isNavActive ? (
                props.icon
              ) : (
                props.iconActive
              )}
            </IconContainer>
            <ItemLabel isActive={Boolean(isNavActive)} colorMain={organization_information.brand_color_hex_code || ''}>
              {props.label}
            </ItemLabel>
          </ItemDetails>
        </ListItem>
      )}
      {!isEmpty(props.subnav) && (
        <NavGroup>
          <GroupLabel>{props.label}</GroupLabel>
          {props.subnav.map((sub: SubNavType, index: number) => {
            const subTo = sub.exact ? sub.to : [props.to, sub.to].join('')
            return !sub.isButton ? (
              <ListItem key={index} to={subTo} onClick={menu.hide}>
                <ItemDetails>
                  <IconContainer>
                    {typeof sub.icon === 'string' && typeof sub.iconActive === 'string' ? (
                      <img src={subTo === active ? sub.iconActive : sub.icon} className="icon" alt="Icon" />
                    ) : subTo === active ? (
                      sub.iconActive
                    ) : (
                      sub.icon
                    )}
                  </IconContainer>
                  <ItemLabel isActive={subTo === active} colorMain={organization_information.brand_color_hex_code || ''}>{sub.label}</ItemLabel>
                </ItemDetails>
              </ListItem>
            ) : (
              <ListItemButton
                key={index}
                onClick={() => {
                  sub.onButtonClick && sub.onButtonClick()
                  menu.hide()
                }}
              >
                <ItemDetails>
                  <IconContainer>
                    {typeof sub.icon === 'string' ? <img src={sub.icon} className="icon" alt="Icon" /> : sub.icon}
                  </IconContainer>
                  <ItemLabel isActive={subTo === active} colorMain={organization_information.brand_color_hex_code || ''}>{sub.label}</ItemLabel>
                </ItemDetails>
              </ListItemButton>
            )
          })}
        </NavGroup>
      )}
    </NavListWrapper>
  )
}

const NavListWrapper = styled.div``

const NavGroup = styled.div`
  margin-top: 40px;
`

const GroupLabel = styled.div`
  font-size: 12px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.lightGrey};
  margin-bottom: 21px;
`

const ItemDetails = styled.div`
  display: flex;
  width: 100%;
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 20px;
  margin-right: 16px;

  .icon {
    height: 20px;
  }
`

const ItemLabel = styled.span<{ isActive?: boolean; colorMain?: string; colorSecondary?: string }>`
  font-size: 16px;
  color: ${({ theme, isActive, colorMain, colorSecondary }) =>
    isActive ? (colorMain ? colorMain : theme.colors.main) : colorSecondary ? colorSecondary : theme.colors.secondary};
  line-height: 19px;
  font-weight: bold;
`

const ListItem = styled(Link)`
  display: flex;
  text-decoration: none;
  height: 20px;
  line-height: 20px;
  cursor: pointer;
  margin-bottom: 1.5rem;
`

const ListItemButton = styled.div`
  display: flex;
  text-decoration: none;
  height: 20px;
  line-height: 20px;
  cursor: pointer;
  margin-bottom: 30px;

  &:last-child {
    margin-bottom: 0;
  }
`

export default NavigationList
